import type { User } from '../types';
import {
  ONGOING_PERIODIC_REVIEW_LABEL,
  ONGOING_VENDOR_PERFORMANCE_REVIEW_LABEL,
} from '../components/vms/vmsNavModel';
import { isCertificationsStaticPath } from './management2Certifications';
import { isManagement2NavPath } from './management2Nav';
import {
  isOutsourcingRegisterPath,
  isVmsAdminConfigGovernancePath,
  VMS_ADMIN_CONFIG_ROUTES,
} from './vmsAdminConfigNav';
import {
  hasAnyVmsRole,
  hasVmsRole,
  isPureVmsAdminUser,
  isVmsAdminOnlyPath,
  isVmsAdminUser,
  isVmsContractManagementPath,
  isVmsDpiaManagementPath,
  isVmsFlowStakeholdersPath,
  isVmsGovernanceAdminUser,
  shouldHideVmsOngoingForUser,
} from '../utils/vmsRole';

export type VmsLifecycleModule =
  | 'pre_onboarding'
  | 'onboarding'
  | 'ongoing'
  | 'contract'
  | 'dpia'
  | 'register'
  | 'dashboard';

/** `/vms/dashboard` and KPI drill-down pages under it (e.g. `/vms/dashboard/vendors`). */
export function isVmsDashboardPath(path: string): boolean {
  return path === '/vms/dashboard' || path.startsWith('/vms/dashboard/');
}

export type VmsMatrixAction = 'view' | 'create' | 'edit' | 'submit' | 'approve' | 'upload';

export type VmsApproverPickerContext =
  | 'pre_onboarding'
  | 'onboarding_annexure'
  | 'onboarding_register_filling'
  | 'onboarding_notification_regulator'
  | 'kyc_annexure'
  | 'contract'
  | 'dpia'
  | 'ongoing_annexure'
  | 'business_head_only';

/** Initiator (`business_owner`) without an operational approver role. */
export function isInitiatorOnlyUser(user: User | null | undefined): boolean {
  if (!user || !hasVmsRole(user, 'business_owner')) return false;
  return !hasAnyVmsRole(user, ['business_head', 'legal', 'dpo', 'kyc_team', 'smf']);
}

/**
 * Role eligibility to be named in approver pickers and act when assigned.
 * Mirrors backend `canActAsVmsWorkflowApprover` (see vms-flow-2 swimlane: BH / Legal / DPO + admin in pickers).
 */
export function canActAsVmsWorkflowApprover(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isInitiatorOnlyUser(user)) return false;
  return hasAnyVmsRole(user, ['business_head', 'legal', 'dpo', 'kyc_team', 'smf', 'admin']);
}

/**
 * Approve / NMI on assigned workflow items (BH, Legal, DPO, or Admin in pickers).
 * Pure VMS admin remains view-only here; assignment-gated screens may use `canActAsVmsWorkflowApprover`.
 */
export function canVmsApproveWorkflow(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isInitiatorOnlyUser(user)) return false;
  if (isPureVmsAdminUser(user)) return false;
  return hasAnyVmsRole(user, ['business_head', 'legal', 'dpo', 'kyc_team', 'smf', 'admin']);
}

export function canVmsAction(
  user: User | null | undefined,
  module: VmsLifecycleModule,
  action: VmsMatrixAction,
): boolean {
  if (!user) return false;
  if (isVmsGovernanceAdminUser(user) && action !== 'approve') {
    if (action === 'view') return true;
    if (action === 'create' || action === 'edit' || action === 'submit') {
      return hasVmsRole(user, 'business_owner') || hasVmsRole(user, 'admin');
    }
  }
  if (isPureVmsAdminUser(user)) {
    return action === 'view';
  }

  switch (action) {
    case 'approve':
      return canActAsVmsWorkflowApprover(user);
    case 'create':
    case 'edit':
    case 'submit':
      return hasVmsRole(user, 'business_owner');
    case 'upload':
      if (canActAsVmsWorkflowApprover(user)) return true;
      if (hasVmsRole(user, 'stakeholder')) return module === 'pre_onboarding' || module === 'onboarding';
      if (hasVmsRole(user, 'legal')) return ['pre_onboarding', 'onboarding', 'contract'].includes(module);
      if (hasVmsRole(user, 'dpo')) return ['pre_onboarding', 'onboarding', 'dpia'].includes(module);
      return hasVmsRole(user, 'business_owner');
    case 'view':
      if (hasVmsRole(user, 'admin')) return true;
      if (hasVmsRole(user, 'business_owner')) return true;
      if (hasVmsRole(user, 'business_head')) return true;
      if (hasVmsRole(user, 'stakeholder')) {
        return module === 'pre_onboarding' || module === 'onboarding';
      }
      if (hasVmsRole(user, 'legal')) {
        return ['pre_onboarding', 'onboarding', 'contract'].includes(module);
      }
      if (hasVmsRole(user, 'dpo')) {
        return ['pre_onboarding', 'onboarding', 'dpia'].includes(module);
      }
      if (hasVmsRole(user, 'smf')) {
        return module === 'onboarding';
      }
      return false;
    default:
      return false;
  }
}

export function canVmsViewRegister(user: User | null | undefined): boolean {
  if (!user) return false;
  return hasVmsRole(user, 'admin') || hasVmsRole(user, 'business_owner');
}

export function canVmsEditRegister(user: User | null | undefined): boolean {
  if (!user) return false;
  return isVmsAdminUser(user);
}

/** Onboarding registered-vendor hub and step pages (pipeline, annexures, NR, register filling). */
function isOnboardingRegisteredVendorPath(path: string): boolean {
  return (
    path === '/vms/onboarding' ||
    /^\/vms\/onboarding\/vendors\/\d+$/.test(path) ||
    /^\/vms\/onboarding\/vendors\/\d+\/annexures$/.test(path) ||
    /^\/vms\/onboarding\/vendors\/\d+\/notification-regulator$/.test(path) ||
    /^\/vms\/onboarding\/vendors\/\d+\/register-filling$/.test(path)
  );
}

function vmsPathAllowedForBusinessHeadSpectator(path: string): boolean {
  return (
    path === '/vms/pre-onboarding/overview' ||
    path === '/vms/pre-onboarding/in-progress' ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-initiation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors-proposal$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-evaluation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/financial-approval$/.test(path) ||
    path === '/vms/pre-onboarding/vendor-initiation' ||
    path === '/vms/vendor-initiation-requests' ||
    path === '/vms/vendor-initiation/annexures/pending' ||
    path === '/vms/vendor-initiation/annexure-7' ||
    /^\/vms\/vendor-initiation\/annexure-7\/\d+$/.test(path) ||
    /^\/vms\/vendor-initiation\/\d+$/.test(path) ||
    path === '/vms/rfp-management' ||
    /^\/vms\/rfp-management\/\d+$/.test(path) ||
    path === '/vms/prospective-vendors' ||
    /^\/vms\/prospective-vendors\/\d+$/.test(path) ||
    /^\/vms\/vendor-proposals\/\d+$/.test(path) ||
    path === '/vms/vendor-evaluations' ||
    path === '/vms/vendor-evaluations/pending/business-head' ||
    /^\/vms\/vendor-evaluations\/\d+$/.test(path) ||
    path === '/vms/financial-approval' ||
    /^\/vms\/financial-approval\/\d+$/.test(path) ||
    /^\/vms\/financial-approval\/\d+\/edit$/.test(path) ||
    isOnboardingRegisteredVendorPath(path) ||
    path === '/vms/onboarding/annexures' ||
    path === '/vms/onboarding/contracts' ||
    path === '/vms/onboarding/contracts/review' ||
    path.startsWith('/vms/onboarding/contracts/review/') ||
    path === '/vms/onboarding/dpia' ||
    path === '/vms/onboarding2' ||
    /^\/vms\/onboarding2\/[^/]+$/.test(path) ||
    /^\/vms\/onboarding2\/[^/]+\/annexures(\/[^/]+)?$/.test(path) ||
    path.startsWith('/vms/ongoing/annexures')
  );
}

function vmsPathAllowedForStakeholderSpectator(path: string): boolean {
  return (
    path === '/vms/pre-onboarding/overview' ||
    path === '/vms/pre-onboarding/vendor-initiation' ||
    path === '/vms/pre-onboarding/in-progress' ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-initiation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors-proposal$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-evaluation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/financial-approval$/.test(path) ||
    path === '/vms/vendor-initiation' ||
    path === '/vms/vendor-initiation/annexure-7' ||
    /^\/vms\/vendor-initiation\/annexure-7\/\d+$/.test(path) ||
    path === '/vms/rfp-management' ||
    /^\/vms\/rfp-management\/\d+$/.test(path) ||
    path === '/vms/vendor-proposals/stakeholder' ||
    /^\/vms\/vendor-proposals\/\d+$/.test(path) ||
    path === '/vms/vendor-evaluations' ||
    path === '/vms/vendor-evaluations/pending/stakeholder' ||
    /^\/vms\/vendor-evaluations\/\d+$/.test(path) ||
    path === '/vms/financial-approval' ||
    /^\/vms\/financial-approval\/\d+$/.test(path) ||
    /^\/vms\/financial-approval\/\d+\/edit$/.test(path) ||
    isOnboardingRegisteredVendorPath(path) ||
    path === '/vms/onboarding/annexures' ||
    path === '/vms/onboarding/contracts' ||
    path === '/vms/onboarding/contracts/review' ||
    path.startsWith('/vms/onboarding/contracts/review/') ||
    path === '/vms/onboarding/dpia' ||
    path === '/vms/onboarding2' ||
    /^\/vms\/onboarding2\/[^/]+$/.test(path) ||
    /^\/vms\/onboarding2\/[^/]+\/annexures(\/[^/]+)?$/.test(path) ||
    path === '/vms/prospective-vendors' ||
    /^\/vms\/prospective-vendors\/\d+$/.test(path) ||
    /^\/vms\/vendor-initiation\/\d+$/.test(path)
  );
}

/** Legal-only spectator: Pre + On + contract submodule (no Ongoing assessment tab, no DPIA). */
function vmsPathAllowedForLegalSpectator(path: string): boolean {
  if (isVmsDpiaManagementPath(path)) return false;
  if (path === '/vms/onboarding/dpia' || path.startsWith('/vms/onboarding/dpia/')) return false;
  return (
    path === '/vms/my-tray' ||
    isVmsDashboardPath(path) ||
    vmsPathAllowedForBusinessHeadSpectator(path) ||
    isVmsContractManagementPath(path) ||
    path === '/vms/onboarding/contracts' ||
    path === '/vms/onboarding/contracts/review' ||
    path.startsWith('/vms/onboarding/contracts/review/')
  );
}

/** DPO-only spectator: Pre + On + all onboarding steps (read progress); DPIA admin module; no contract form builder. */
function vmsPathAllowedForDpoSpectator(path: string): boolean {
  if (isVmsContractManagementPath(path)) return false;
  return (
    path === '/vms/my-tray' ||
    isVmsDashboardPath(path) ||
    vmsPathAllowedForBusinessHeadSpectator(path) ||
    isVmsDpiaManagementPath(path) ||
    path === '/vms/onboarding/dpia'
  );
}

/** KYC Team: onboarding + ongoing only (assigned Annexure 6 vendors); no pre-onboarding, contract, or DPIA. */
function vmsPathAllowedForKycSpectator(path: string): boolean {
  if (isVmsContractManagementPath(path)) return false;
  if (isVmsDpiaManagementPath(path)) return false;
  if (path === '/vms/onboarding/dpia' || path.startsWith('/vms/onboarding/dpia/')) return false;
  if (path === '/vms/onboarding/contracts' || path.startsWith('/vms/onboarding/contracts')) return false;
  if (path.startsWith('/vms/pre-onboarding')) return false;
  if (path.startsWith('/vms/vendor-initiation')) return false;
  if (path.startsWith('/vms/rfp-management')) return false;
  if (path.startsWith('/vms/vendor-evaluations')) return false;
  if (path.startsWith('/vms/financial-approval')) return false;
  if (path.startsWith('/vms/prospective-vendors')) return false;
  if (path.startsWith('/vms/vendor-proposals')) return false;
  return (
    path === '/vms/my-tray' ||
    isVmsDashboardPath(path) ||
    isOnboardingRegisteredVendorPath(path) ||
    path === '/vms/onboarding/annexures' ||
    path === '/vms/onboarding2' ||
    /^\/vms\/onboarding2\/[^/]+$/.test(path) ||
    /^\/vms\/onboarding2\/[^/]+\/annexures(\/[^/]+)?$/.test(path) ||
    path === '/vms/ongoing' ||
    path.startsWith('/vms/ongoing/')
  );
}

/** SMF: dashboard, My Tray, and onboarding vendors where assigned as NR approver. */
function vmsPathAllowedForSmfSpectator(path: string): boolean {
  if (path.startsWith('/vms/pre-onboarding')) return false;
  if (path.startsWith('/vms/vendor-initiation')) return false;
  if (path.startsWith('/vms/rfp-management')) return false;
  if (path.startsWith('/vms/vendor-evaluations')) return false;
  if (path.startsWith('/vms/financial-approval')) return false;
  if (path.startsWith('/vms/prospective-vendors')) return false;
  if (path.startsWith('/vms/vendor-proposals')) return false;
  if (path === '/vms/ongoing' || path.startsWith('/vms/ongoing/')) return false;
  if (isVmsContractManagementPath(path)) return false;
  if (isVmsDpiaManagementPath(path)) return false;
  if (path === '/vms/onboarding/dpia' || path.startsWith('/vms/onboarding/dpia/')) return false;
  if (path === '/vms/onboarding/contracts' || path.startsWith('/vms/onboarding/contracts')) return false;
  return (
    path === '/vms/my-tray' ||
    isVmsDashboardPath(path) ||
    isOnboardingRegisteredVendorPath(path) ||
    path === '/vms/onboarding/annexures' ||
    path === '/vms/onboarding2' ||
    /^\/vms\/onboarding2\/[^/]+$/.test(path) ||
    /^\/vms\/onboarding2\/[^/]+\/annexures(\/[^/]+)?$/.test(path)
  );
}

/** Union route guard for non–business-owner VMS users (multi-role unions paths). */
export function isVmsPathAllowedForUser(user: User | null | undefined, path: string): boolean {
  if (!user || !path.startsWith('/vms')) return false;
  if (hasVmsRole(user, 'business_owner')) return true;

  if (shouldHideVmsOngoingForUser(user) && (path === '/vms/ongoing' || path.startsWith('/vms/ongoing/'))) {
    return false;
  }

  if (!isVmsAdminUser(user) && isVmsAdminOnlyPath(path)) {
    // DPIA Management registry (admin/DPO/BO) or definition editor sub-routes (broader roles).
    if (
      !(
        isVmsDpiaManagementPath(path) &&
        ((path === '/vms/dpia-management' && showVmsDpiaManagementRegistryLink(user)) ||
          (path !== '/vms/dpia-management' && showVmsManagementDpiaLink(user)))
      )
    ) {
      return false;
    }
  }

  if (isVmsFlowStakeholdersPath(path) && !hasVmsRole(user, 'business_owner') && !isVmsAdminUser(user)) {
    return false;
  }

  if (isOutsourcingRegisterPath(path) || path === VMS_ADMIN_CONFIG_ROUTES.register) {
    return canVmsViewRegister(user);
  }

  if (path === '/vms' || isVmsDashboardPath(path)) {
    return (
      hasVmsRole(user, 'business_head') ||
      hasVmsRole(user, 'stakeholder') ||
      isVmsAdminUser(user) ||
      hasVmsRole(user, 'legal') ||
      hasVmsRole(user, 'dpo') ||
      hasVmsRole(user, 'kyc_team') ||
      hasVmsRole(user, 'smf')
    );
  }

  if (isPureVmsAdminUser(user)) {
    return (
      path === '/vms' ||
      isVmsDashboardPath(path) ||
      path === '/vms/my-tray' ||
      path === '/vms/ongoing' ||
      path.startsWith('/vms/ongoing/') ||
      isOutsourcingRegisterPath(path) ||
      isVmsAdminOnlyPath(path) ||
      isVmsContractManagementPath(path) ||
      isVmsDpiaManagementPath(path) ||
      isVmsFlowStakeholdersPath(path) ||
      isCertificationsStaticPath(path) ||
      isManagement2NavPath(path) ||
      vmsPathAllowedForBusinessHeadSpectator(path) ||
      vmsPathAllowedForStakeholderSpectator(path)
    );
  }

  let allowed = false;
  if (hasVmsRole(user, 'business_head')) {
    allowed =
      allowed ||
      vmsPathAllowedForBusinessHeadSpectator(path) ||
      path === '/vms/my-tray' ||
      path === '/vms/ongoing' ||
      path.startsWith('/vms/ongoing/');
  }
  if (hasVmsRole(user, 'stakeholder')) {
    allowed = allowed || vmsPathAllowedForStakeholderSpectator(path);
  }
  if (hasVmsRole(user, 'legal')) {
    allowed = allowed || vmsPathAllowedForLegalSpectator(path);
  }
  if (hasVmsRole(user, 'dpo')) {
    allowed = allowed || vmsPathAllowedForDpoSpectator(path);
  }
  if (hasVmsRole(user, 'kyc_team')) {
    allowed = allowed || vmsPathAllowedForKycSpectator(path);
  }
  if (hasVmsRole(user, 'smf')) {
    allowed = allowed || vmsPathAllowedForSmfSpectator(path);
  }
  if (isVmsAdminUser(user)) {
    allowed =
      allowed ||
      isVmsDashboardPath(path) ||
      isVmsAdminOnlyPath(path) ||
      isVmsAdminConfigGovernancePath(path) ||
      isVmsContractManagementPath(path) ||
      isVmsDpiaManagementPath(path);
  }

  return allowed;
}

export function showVmsRegisterInNav(user: User | null | undefined): boolean {
  return canVmsViewRegister(user);
}

/** Dashboard route — BH / Legal / DPO / stakeholder / admin (not initiator-only unless also listed). */
export function canAccessVmsDashboard(user: User | null | undefined): boolean {
  if (!user) return false;
  if (hasVmsRole(user, 'business_owner')) return true;
  return isVmsPathAllowedForUser(user, '/vms/dashboard');
}

export function showVmsOngoingInNav(user: User | null | undefined): boolean {
  if (!user) return false;
  if (shouldHideVmsOngoingForUser(user)) return false;
  // Pure Legal/DPO spectators use My Tray + deep links, not the Ongoing Management hub tab (0b6d046).
  if (hasVmsRole(user, 'legal') && !hasVmsRole(user, 'business_head') && !hasVmsRole(user, 'business_owner')) {
    return false;
  }
  if (hasVmsRole(user, 'dpo') && !hasVmsRole(user, 'business_head') && !hasVmsRole(user, 'business_owner')) {
    return false;
  }
  if (hasVmsRole(user, 'kyc_team') && !hasVmsRole(user, 'business_head') && !hasVmsRole(user, 'business_owner')) {
    return true;
  }
  return (
    hasVmsRole(user, 'business_owner') ||
    hasVmsRole(user, 'business_head') ||
    isVmsAdminUser(user)
  );
}

/** Primary nav Onboarding tab (DPO / Legal see assigned-vendor progress hub). */
export function showVmsOnboardingInNav(user: User | null | undefined): boolean {
  if (!user) return false;
  return (
    hasVmsRole(user, 'business_owner') ||
    hasVmsRole(user, 'business_head') ||
    hasVmsRole(user, 'stakeholder') ||
    hasVmsRole(user, 'legal') ||
    hasVmsRole(user, 'dpo') ||
    hasVmsRole(user, 'kyc_team') ||
    hasVmsRole(user, 'smf') ||
    isVmsAdminUser(user)
  );
}

/** Primary nav Pre Onboarding group (not shown for pure SMF / KYC approver UX). */
export function showVmsPreOnboardingInNav(user: User | null | undefined): boolean {
  if (!user) return false;
  if (
    hasVmsRole(user, 'smf') &&
    !hasAnyVmsRole(user, ['business_head', 'business_owner', 'stakeholder', 'legal', 'dpo', 'admin'])
  ) {
    return false;
  }
  if (
    hasVmsRole(user, 'kyc_team') &&
    !hasAnyVmsRole(user, ['business_head', 'business_owner', 'stakeholder', 'legal', 'dpo', 'admin'])
  ) {
    return false;
  }
  return (
    hasVmsRole(user, 'business_owner') ||
    hasVmsRole(user, 'business_head') ||
    hasVmsRole(user, 'stakeholder') ||
    hasVmsRole(user, 'legal') ||
    hasVmsRole(user, 'dpo') ||
    isVmsAdminUser(user)
  );
}

/** Tray section labels this user may see when expanding a vendor row. */
export function myTrayApproverSectionLabels(user: User | null | undefined): string[] {
  const labels: string[] = [];
  if (showVmsPreOnboardingInNav(user)) labels.push('Pre Onboarding');
  if (showVmsOnboardingInNav(user)) labels.push('Onboarding');
  if (showVmsOngoingInNav(user)) labels.push('Ongoing Management');
  return labels;
}

/** e.g. "the Onboarding section" or "the Pre Onboarding, Onboarding, or Ongoing Management sections". */
export function formatMyTraySectionExpandHint(sectionLabels: string[]): string | null {
  if (sectionLabels.length === 0) return null;
  if (sectionLabels.length === 1) return `the ${sectionLabels[0]} section`;
  if (sectionLabels.length === 2) {
    return `the ${sectionLabels[0]} or ${sectionLabels[1]} sections`;
  }
  const last = sectionLabels[sectionLabels.length - 1];
  return `the ${sectionLabels.slice(0, -1).join(', ')}, or ${last} sections`;
}

export function showVmsManagementContractLink(user: User | null | undefined): boolean {
  if (!user) return false;
  return (
    hasVmsRole(user, 'business_owner') ||
    hasVmsRole(user, 'business_head') ||
    hasVmsRole(user, 'legal') ||
    isVmsAdminUser(user)
  );
}

export function showVmsManagementDpiaLink(user: User | null | undefined): boolean {
  if (!user) return false;
  return (
    hasVmsRole(user, 'business_owner') ||
    hasVmsRole(user, 'business_head') ||
    hasVmsRole(user, 'dpo') ||
    isVmsAdminUser(user)
  );
}

/** DPIA Management vendor registry (approved applicable fillings) — admin, DPO, and Department RM. */
export function showVmsDpiaManagementRegistryLink(user: User | null | undefined): boolean {
  if (!user) return false;
  return (
    isVmsGovernanceAdminUser(user) ||
    hasVmsRole(user, 'dpo') ||
    hasVmsRole(user, 'business_owner')
  );
}

export function showVmsManagementFullFlyout(user: User | null | undefined): boolean {
  if (!user) return false;
  return hasVmsRole(user, 'business_owner') || isVmsAdminUser(user);
}

export function showVmsPreOnboardingNewInitiation(user: User | null | undefined): boolean {
  return hasVmsRole(user, 'business_owner');
}

/** Roles included in approver picker API calls for a given workflow context (BH always eligible). */
export function approverPickerRoleSlugs(context: VmsApproverPickerContext): string[] {
  switch (context) {
    case 'contract':
      return ['business_head', 'legal', 'admin'];
    case 'dpia':
      return ['dpo'];
    case 'kyc_annexure':
      return ['business_head', 'admin'];
    case 'onboarding_register_filling':
      return ['admin'];
    case 'onboarding_notification_regulator':
      return ['business_head', 'smf'];
    case 'pre_onboarding':
    case 'onboarding_annexure':
      return ['business_head', 'dpo', 'legal', 'admin'];
    case 'ongoing_annexure':
      return ['business_head', 'admin'];
    case 'business_head_only':
      return ['business_head'];
    default:
      return ['business_head'];
  }
}

export function approverPickerHint(context: VmsApproverPickerContext): string {
  switch (context) {
    case 'contract':
      return 'Choose up to five approvers (business head, legal, or admin).';
    case 'dpia':
      return 'Choose up to five DPO approvers.';
    case 'kyc_annexure':
      return 'Choose up to five approvers (business head or admin). KYC Team members are assigned separately.';
    case 'onboarding_register_filling':
      return 'Choose up to five VMS admin approvers.';
    case 'onboarding_notification_regulator':
      return 'Select business heads or SMF users — one row per approver, searchable list (max 5).';
    case 'pre_onboarding':
    case 'onboarding_annexure':
      return 'Choose up to five approvers (business head, DPO, legal, or admin).';
    case 'ongoing_annexure':
      return 'Choose up to five approvers (business head or admin).';
    default:
      return 'Choose up to five business heads.';
  }
}

export function approverPickerEmptyMessage(context: VmsApproverPickerContext): string {
  switch (context) {
    case 'contract':
      return 'No approvers found. Assign users the business_head, legal, or admin VMS role.';
    case 'dpia':
      return 'No DPO users found. Assign users the dpo VMS role.';
    case 'kyc_annexure':
      return 'No approvers found. Assign users the business_head or admin VMS role.';
    case 'onboarding_register_filling':
      return 'No VMS admin users found. Assign users the admin VMS role.';
    case 'onboarding_notification_regulator':
      return 'No approvers found. Assign users the business_head or smf VMS role.';
    case 'pre_onboarding':
    case 'onboarding_annexure':
      return 'No approvers found. Assign users the business_head, dpo, legal, or admin VMS role.';
    case 'ongoing_annexure':
      return 'No approvers found. Assign users the business_head or admin VMS role.';
    default:
      return 'No business heads found. Assign users the business_head VMS role.';
  }
}

const MANAGEMENT_FLYOUT_ROLE_SCOPED_NAMES = new Set([
  'Contract Management',
  'DPIA Management',
]);

const MANAGEMENT_FLYOUT_INITIATOR_BUNDLE_NAMES = new Set([
  'Flow Stakeholders',
  'My flows & stakeholders',
  'Vendor Exit',
  'Audit Logs',
]);

/** Strip Management flyout links the user must not see (DPO → DPIA only, Legal → Contract only). */
export function filterVmsNavLinkForUser(
  user: User | null | undefined,
  item: { name: string; href: string },
): boolean {
  if (!user) return false;
  if (MANAGEMENT_FLYOUT_ROLE_SCOPED_NAMES.has(item.name)) {
    if (item.name === 'Contract Management' || isVmsContractManagementPath(item.href)) {
      return showVmsManagementContractLink(user);
    }
    if (item.name === 'DPIA Management' || isVmsDpiaManagementPath(item.href)) {
      return showVmsManagementDpiaLink(user) || showVmsDpiaManagementRegistryLink(user);
    }
  }
  if (item.name === 'Certifications & licenses') {
    return (
      showVmsManagementFullFlyout(user) ||
      hasVmsRole(user, 'business_head') ||
      hasVmsRole(user, 'stakeholder')
    );
  }
  if (MANAGEMENT_FLYOUT_INITIATOR_BUNDLE_NAMES.has(item.name)) {
    return showVmsManagementFullFlyout(user);
  }
  if (item.name === 'Onboarding' || item.href === '/vms/onboarding') {
    return showVmsOnboardingInNav(user);
  }
  if (
    item.name === 'Ongoing Management' ||
    item.name === ONGOING_VENDOR_PERFORMANCE_REVIEW_LABEL ||
    item.name === ONGOING_PERIODIC_REVIEW_LABEL ||
    item.href === '/vms/ongoing' ||
    item.href.startsWith('/vms/ongoing?') ||
    item.href === '/vms/ongoing-management'
  ) {
    return showVmsOngoingInNav(user);
  }
  return true;
}

/** Management flyout: limited items for DPO / Legal (not full admin bundle). */
export function showVmsManagementFlyoutForUser(user: User | null | undefined): boolean {
  if (!user) return false;
  return (
    showVmsManagementFullFlyout(user) ||
    showVmsDpiaManagementRegistryLink(user) ||
    showVmsManagementContractLink(user)
  );
}
